import { useCallback } from "react";
import { toast } from "@jsq/ds/core";
import { useChangeAndResetDocumentDataMutation } from "~/graphql/supergraphHooks";
import { type DocumentResetInfoInput } from "~/graphql/supergraphTypes";

type UseChangeAndResetDocumentDataOptions = {
  caseId: number;
  documentId: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

type ChangeAndResetParams = {
  resetInfo: DocumentResetInfoInput;
};

type UseChangeAndResetDocumentDataReturn = {
  changeAndReset: (params: ChangeAndResetParams) => Promise<boolean>;
  isLoading: boolean;
};

/**
 * Hook to handle changing document data (profile, document type, or sub-type)
 * and resetting the associated metadata via backend mutation.
 *
 * This replaces the previous frontend-only reset logic that used
 * resetDocumentFormFields and shouldClearTaxMetadata.
 */
export const useChangeAndResetDocumentData = ({
  caseId,
  documentId,
  onSuccess,
  onError,
}: UseChangeAndResetDocumentDataOptions): UseChangeAndResetDocumentDataReturn => {
  const [mutate, { loading }] = useChangeAndResetDocumentDataMutation({
    refetchQueries: ["GetComplianceCase", "GetTaxReportingData", "GetTaxDataSourceAssignments"],
    onCompleted: () => {
      toast.success("Document data reset successfully");
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message);
      onError?.(error);
    },
  });

  const changeAndReset = useCallback(
    async ({ resetInfo }: ChangeAndResetParams): Promise<boolean> => {
      if (!documentId || !caseId) {
        toast.error("Missing document or case information");
        return false;
      }

      try {
        await mutate({
          variables: {
            caseId,
            documentId,
            resetInfo,
          },
        });
        return true;
      } catch {
        return false;
      }
    },
    [mutate, caseId, documentId],
  );

  return { changeAndReset, isLoading: loading };
};
